import React, { useEffect, useState } from 'react';
import { Styled } from './';
import { motion, useAnimation } from 'motion/react'
import { useOrderDetails } from '../../reactQuery/queries';
import { useRouter } from 'next/router';
import { useQueryClient } from 'react-query';
import { ServerStateKeysEnum } from '../../reactQuery/constants';
import { IoMdCloseCircle } from 'react-icons/io';
import { useI18n } from 'contexts/i18n';

/**
 * OrderSearch component allows user to search for order by number
 * User input is restricted to numbers and only N, E, G (or their lowercase versions) as the first character
 * When user submits the search, component uses useOrderDetails hook to fetch order details and useRouter hook to navigate to order details page
 * If input is invalid, form will shake to indicate error
 * Loading spinner is displayed while fetching order details
 */
function OrderSearch() {
  const [inputValue, setInputValue] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const shakingAnimation = useAnimation();
  const { isError, isLoading, isSuccess } = useOrderDetails(orderNumber);
  const queryClient = useQueryClient();
  const router = useRouter();
  const i18n = useI18n();
  // Start shaking animation when order number is not valid
  useEffect(() => {
    shakingAnimation.start({
      x: [null, -10, 10, -10, 10, -8, 0],
      transition: { duration: 0.5, times: [0, 0.1, 0.2, 0.3, 0.8, 0.9] },
    });
  }, [shakingAnimation, isError]);

  // Navigate to order details page when fetch is successful
  useEffect(() => {
    if (isSuccess) {
      router.push(`/orderdetails/${inputValue}`);
    }
  }, [isSuccess, inputValue, router]);

  // Check if the first character is a number or N, E, G or A (case-insensitive)
  const isFirstCharValid = (newValue: string) => {
    const firstChar = newValue[0];
    return /^[\dnegaNEGA]/.test(firstChar);
  };

  // Check if all the characters after first one are valid (numbers)
  const isInputValid = (newValue: string) => {
    return /^\d*$/.test(newValue.slice(1));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    // Check if the first character is valid and the entire input is valid. Also makes it uppercase
    if (isFirstCharValid(inputValue) && isInputValid(inputValue)) {
      setInputValue(inputValue.toUpperCase());
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, inputValue: string) => {
    // Clear input when user hits backspace if input is empty
    if (event.key === 'Backspace' && inputValue.length === 1) {
      setInputValue('');
    }
    // When user hits enter, set the order number
    if (event.key === 'Enter' && inputValue.length > 0) {
      setOrderNumber(inputValue);
    }
  };

  const clearInput = () => {
    // https://tanstack.com/query/v4/docs/react/guides/query-cancellation#manual-cancellation
    queryClient.cancelQueries({
      queryKey: [`${ServerStateKeysEnum.orderDetails}-${orderNumber}`],
    });
    setInputValue(_previousValue => '');
  };

  const isInvalidInput =
    inputValue.length > 0 && inputValue === orderNumber && isError;

  return (
    <motion.div animate={isError ? shakingAnimation : undefined}>
      <Styled.OrderSearchWrapper isInvalidInput={isInvalidInput}>
        <input
          type="text"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={event => handleKeyDown(event, inputValue)}
          placeholder={i18n['Search by order number']}
          data-testid="order-search-input"
        />
        {!isLoading ? null : <Styled.SearchLoadingSpinner />}
        {/* Due to strict regex validation,
          the browser prevents input field to be cleared in a traditional way
          (eg.: ctrl + A + backspace, ctrl + backspace, etc...), only allows backspace, so clear button added here.
          The button copied from react-datepicker to keep style consistent.
          */}
        {inputValue && (
          <IoMdCloseCircle aria-label="Close" onClick={clearInput} size={20} />
        )}
      </Styled.OrderSearchWrapper>
    </motion.div>
  );
}

export default OrderSearch;
